import { useCallback, useEffect, useRef } from "react";
import type { QueryClient } from "@tanstack/react-query";
import type { useUpdateProject } from "@/api/api";
import type { ActionStore } from "@/stores/ActionStore";
import { bumpProjectDetailsTimestamp, pickNewerProjectDetails, PROJECT_STORE_VERSIONS_KEY, withFreshProjectStoreVersions } from "@/services/projectDetails";
import type { ProjectDetailsResponse } from "@/types/project";
import { toastError } from "@/utils/toast";

/** Optimistic MobX project save with debounced POST /project (library/mainspace drops). */

const SAVE_DEBOUNCE_MS = 300;

type UpdateProjectMutation = ReturnType<typeof useUpdateProject>;

export function useOptimisticProjectSave(
  projectId: string | null,
  updateProjectMutation: UpdateProjectMutation,
  actionStore: ActionStore,
  queryClient: QueryClient
) {
  const pendingRef = useRef<ProjectDetailsResponse | null>(null);
  const flushInFlightRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastConfirmedRef = useRef<ProjectDetailsResponse | null>(null);

  useEffect(() => {
    lastConfirmedRef.current = null;
  }, [projectId]);

  const syncProjectQueryCache = useCallback(
    (details: ProjectDetailsResponse) => {
      const id = details.project_id;
      if (!id) {
        return;
      }

      queryClient.setQueryData<ProjectDetailsResponse>(["project", id], (old) => pickNewerProjectDetails(old, details) ?? details);
    },
    [queryClient]
  );

  const flush = useCallback(async () => {
    if (flushInFlightRef.current) {
      return;
    }

    const body = pendingRef.current;
    const saveProjectId = body?.project_id;
    if (!body || !saveProjectId) {
      return;
    }

    pendingRef.current = null;
    flushInFlightRef.current = true;

    try {
      const result = await updateProjectMutation.mutateAsync({ projectId: saveProjectId, body });
      lastConfirmedRef.current = result;

      // Re-baseline so the next save doesn't 409 against this user's own just-completed write.
      const freshVersions = result.project?.[PROJECT_STORE_VERSIONS_KEY];
      if (freshVersions !== undefined) {
        pendingRef.current = withFreshProjectStoreVersions(pendingRef.current, freshVersions);
        if (actionStore.projectDetails?.project_id === saveProjectId) {
          const updated = withFreshProjectStoreVersions(actionStore.projectDetails, freshVersions);
          if (updated) {
            actionStore.setProjectDetails(updated);
            syncProjectQueryCache(updated);
          }
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save project";
      toastError(message);

      const rollback = lastConfirmedRef.current;
      if (rollback) {
        actionStore.setProjectDetails(rollback);
        syncProjectQueryCache(rollback);
      }

      pendingRef.current = null;
    } finally {
      flushInFlightRef.current = false;
      if (pendingRef.current) {
        void flush();
      }
    }
  }, [updateProjectMutation, actionStore, syncProjectQueryCache]);

  const flushRef = useRef(flush);
  flushRef.current = flush;

  const scheduleFlush = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      void flush();
    }, SAVE_DEBOUNCE_MS);
  }, [flush]);

  const commitOptimisticProjectSave = useCallback(
    (next: ProjectDetailsResponse) => {
      if (lastConfirmedRef.current === null && actionStore.projectDetails?.project_id === projectId) {
        lastConfirmedRef.current = actionStore.projectDetails;
      }

      const withTimestamp = bumpProjectDetailsTimestamp(next);
      actionStore.setProjectDetails(withTimestamp);
      syncProjectQueryCache(withTimestamp);
      pendingRef.current = withTimestamp;
      scheduleFlush();
    },
    [actionStore, projectId, scheduleFlush, syncProjectQueryCache]
  );

  useEffect(() => {
    const closingProjectId = projectId;

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      // Persist pending drop save when leaving the project (no effect during normal editing).
      if (pendingRef.current?.project_id === closingProjectId) {
        void flushRef.current();
      }
    };
  }, [projectId]);

  return { commitOptimisticProjectSave };
};
